import { Plus } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';
import { useTenantUsersTab } from '../../../features/tenant/hooks/useTenantUsersTab';
import { CreateTenantUserDialog } from '../CreateTenantUserDialog';
import { TenantUserRow } from '../TenantUserRow';
import './TenantUsersTab.css';

export function TenantUsersTab() {
  const {
    translate,
    usersQuery,
    createUserMutation,
    filters,
    filteredUsers,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    handleCreateUser,
    userCount,
    maxUsers,
    isTenantFull,
    hasUsers,
  } = useTenantUsersTab();

  if (usersQuery.isLoading) {
    return (
      <p className="tenant-users__status">
        {translate('organisationManagement.users.loading')}
      </p>
    );
  }

  if (usersQuery.isError) {
    return (
      <p className="tenant-users__status" role="alert">
        {translate('organisationManagement.users.loadError')}
      </p>
    );
  }

  return (
    <>
      <div className="tenant-users">
        <div className="tenant-users__toolbar">
          <LiksSearch
            className="tenant-users__search"
            value={filters.searchInput}
            onChange={filters.handleSearchChange}
            placeholder={translate(
              'organisationManagement.users.searchPlaceholder',
            )}
            aria-label={translate('organisationManagement.users.searchLabel')}
            clearLabel={translate('common.search.clear')}
            variant="on-light"
          />
          <LiksDropdown
            className="tenant-users__status-filter"
            variant="on-light"
            value={filters.statusFilter}
            onChange={filters.handleStatusFilterChange}
            options={[
              {
                value: 'all',
                label: translate(
                  'organisationManagement.users.statusFilter.all',
                ),
              },
              {
                value: 'enabled',
                label: translate('organisationManagement.users.enabled'),
              },
              {
                value: 'disabled',
                label: translate('organisationManagement.users.disabled'),
              },
            ]}
            aria-label={translate(
              'organisationManagement.users.statusFilter.label',
            )}
          />
          <LiksButton
            className="tenant-users__create-button"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={isTenantFull}
          >
            <Plus strokeWidth={2} aria-hidden />
            {translate('organisationManagement.users.create.open')}
          </LiksButton>
          <span
            className={[
              'tenant-users__count',
              isTenantFull && 'tenant-users__count--danger',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {maxUsers != null
              ? translate('organisationManagement.users.userCountWithMax', {
                  count: String(userCount),
                  max: String(maxUsers),
                })
              : translate('organisationManagement.users.userCount', {
                  count: String(userCount),
                })}
          </span>
        </div>

        {!hasUsers ? (
          <p className="tenant-users__empty">
            {translate('organisationManagement.users.empty')}
          </p>
        ) : filteredUsers.length === 0 ? (
          <p className="tenant-users__empty">
            {translate('organisationManagement.users.noSearchResults')}
          </p>
        ) : (
          <ul
            className="tenant-users__list liks-scrollbar"
            aria-label={translate('organisationManagement.tabs.users')}
          >
            {filteredUsers.map((user) => (
              <TenantUserRow key={user.id} user={user} />
            ))}
          </ul>
        )}
      </div>

      {isCreateDialogOpen && (
        <CreateTenantUserDialog
          onCreate={handleCreateUser}
          onClose={() => setIsCreateDialogOpen(false)}
          isSubmitting={createUserMutation.isPending}
        />
      )}
    </>
  );
}
